import React, { useEffect } from "react";

const experienceData = [
  {
    title: "Founder & Web Developer",
    company: "Dashly Studio",
    location: "Remote",
    date: "AUG 2025 – PRESENT",
  },
  {
    title: "Public Relations Manager",
    company: "ProNGO!",
    location: "Remote",
    date: "JUN 2025 – DEC 2025",
  },
  {
    title: "Student Ambassador (Registration Support)",
    company: "University of Aberdeen",
    location: "Aberdeen, Scotland, UK",
    date: "JAN 2025 – PRESENT",
  },
  {
    title: "Office Administrator & Social Media Assistant",
    company: "JOR Property ltd",
    location: "Aberdeen, Scotland, UK",
    date: "MAR 2024 – SEP 2024",
  },
  {
    title: "Web Developer",
    company: "Freelance",
    location: "Remote",
    date: "JAN 2023 – PRESENT",
  },
  {
    title: "Personal Assistant",
    company: "Barwell Enterprises",
    location: "Aberdeen, Scotland, UK",
    date: "OCT 2022 – MAR 2023",
  },
  {
    title: "Administrative Assistant",
    company: "Egalite International",
    location: "Remote",
    date: "AUG 2022 – DEC 2022",
  },
];

export default function Experience() {
  useEffect(() => {
    const section = document.getElementById("experience");
    if (!section) return;

    const timeline = section.querySelector(".timeline");
    if (!timeline) return;

    const dots = Array.from(timeline.querySelectorAll(".timeline-dot"));
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    timeline.classList.add("timeline-animate");

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const lerp = (start, end, factor) => start + (end - start) * factor;

    if (prefersReducedMotion) {
      timeline.style.setProperty("--timeline-progress", "1");
      dots.forEach((dot) => {
        dot.classList.add("is-visible");
        dot.classList.add("is-active");
      });
      return;
    }

    let animationFrameId = null;
    let currentProgress = 0;
    let targetProgress = 0;
    let lastFrameTime = null;

    const getRawProgress = () => {
      const rect = timeline.getBoundingClientRect();
      if (rect.height <= 0) return 0;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const centerLine = viewportHeight * 0.5;
      const passedDistance = centerLine - rect.top;
      return clamp(passedDistance / rect.height, 0, 1);
    };

    const renderTimeline = (progress) => {
      const rect = timeline.getBoundingClientRect();
      const filledHeight = rect.height * progress;

      timeline.style.setProperty("--timeline-progress", progress.toFixed(3));

      dots.forEach((dot) => {
        const dotRect = dot.getBoundingClientRect();
        const dotCenterInTimeline = dotRect.top - rect.top + dotRect.height / 2;
        dot.classList.toggle("is-active", dotCenterInTimeline <= filledHeight + 2);
      });
    };

    const animateProgress = (timestamp) => {
      const delta = lastFrameTime ? timestamp - lastFrameTime : 16;
      lastFrameTime = timestamp;
      const ease = 1 - Math.exp(-delta / 58);

      currentProgress = lerp(currentProgress, targetProgress, ease);

      if (Math.abs(targetProgress - currentProgress) < 0.0006) {
        currentProgress = targetProgress;
      }

      renderTimeline(currentProgress);

      if (currentProgress !== targetProgress) {
        animationFrameId = window.requestAnimationFrame(animateProgress);
      } else {
        animationFrameId = null;
        lastFrameTime = null;
      }
    };

    const startAnimation = () => {
      if (animationFrameId !== null) return;
      lastFrameTime = null;
      animationFrameId = window.requestAnimationFrame(animateProgress);
    };

    const requestUpdate = () => {
      targetProgress = getRawProgress();
      startAnimation();
    };

    const handleResize = () => {
      requestUpdate();
    };

    const dotObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-visible", entry.isIntersecting);
        });
        requestUpdate();
      },
      {
        threshold: 0.01,
        rootMargin: "0px 0px -5% 0px",
      }
    );

    dots.forEach((dot) => dotObserver.observe(dot));

    currentProgress = getRawProgress();
    targetProgress = currentProgress;
    renderTimeline(currentProgress);
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      dotObserver.disconnect();
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", handleResize);
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      lastFrameTime = null;
    };
  }, []);

  return (
    <section id="experience" className="about-section">
      <h2 className="section-title">Experience</h2>
      <div className="timeline">
        {experienceData.map((exp, index) => (
          <div key={index} className="timeline-item">
            <div className="timeline-dot"></div>
            <div className="timeline-content">
              <h3 className="timeline-title">{exp.title}</h3>
              <p className="timeline-company">{exp.company}</p>
              <p className="timeline-location">{exp.location}</p>
              <p className="job-type">{exp.jobType}</p>
            </div>
            <div className="timeline-date">{exp.date}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
